import { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const VerifyEmailChange = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      setDebugInfo('Token parameter missing from URL');
      return;
    }
    
    verifyEmailChange(token);
  }, [searchParams]);

  const verifyEmailChange = async (token) => {
    setDebugInfo(`Verifying with token: ${token.substring(0, 20)}...`);
    
    try {
      const response = await apiClient.post(`/users/email/verify?token=${encodeURIComponent(token)}`);
      
      setStatus('success');
      setMessage(response.data.message || 'Email address successfully changed!');
      setNewEmail(response.data.new_email);
      setDebugInfo('Verification successful');
      
      toast.success('Email address changed successfully!');
      
      // If user is logged in, refresh their session to get updated email
      if (user) {
        setTimeout(() => {
          window.location.href = '/profile';
        }, 2000);
      }
    } catch (error) {
      console.error('[VerifyEmailChange] API error:', error);
      console.error('[VerifyEmailChange] API error:', error);
      setStatus('error');
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to verify email change';
      setMessage(errorMsg);
      setDebugInfo(`Error: ${errorMsg} (Status: ${error.response?.status || 'Network error'})`);
      toast.error(errorMsg);
    }
  };

  // Render component - this ensures page is never blank
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Verification
          </CardTitle>
          <CardDescription>
            Verifying your new email address
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'verifying' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              <p className="text-gray-600">Verifying your email address...</p>
              <p className="text-xs text-gray-400">{debugInfo}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium text-green-900">
                      {message}
                    </p>
                    {newEmail && (
                      <p className="text-sm text-green-800">
                        Your new email address: <strong>{newEmail}</strong>
                      </p>
                    )}
                    <p className="text-sm text-green-700">
                      You can now use this email to log in.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate('/profile')}
                  className="w-full"
                >
                  Go to Profile
                </Button>
                {!user && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="w-full"
                  >
                    Log In with New Email
                  </Button>
                )}
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-5 w-5 text-red-600" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-medium text-red-900">
                      Verification Failed
                    </p>
                    <p className="text-sm text-red-800">
                      {message}
                    </p>
                    <p className="text-sm text-red-700">
                      Common reasons:
                    </p>
                    <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                      <li>The verification link has expired (24 hour limit)</li>
                      <li>The link has already been used</li>
                      <li>The email change was cancelled</li>
                    </ul>
                    {debugInfo && (
                      <p className="text-xs text-red-600 mt-2 font-mono bg-red-100 p-2 rounded">
                        Debug: {debugInfo}
                      </p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>

              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => navigate('/account-settings')}
                  className="w-full"
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Go to Home
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailChange;
